import { useEffect, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/RecordingRulesSlice";
import { RootState } from "src/redux/store";
import {
  useDeleteRecordingRulesMutation,
  useGetAllRecordingRulesQuery,
} from "src/services/RecordingRulesServices";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddRecordingRulesFormWrapper from "../Add/AddRecordingRulesFormWrapper";
import EditRecordingRulesFormWrapper from "../Edit/EditRecordingRulesFormWrapper";
import RecordingRulesListing from "./RecordingRulesListing";
import moment from "moment";

type Props = {};

const RecordingRulesListingWrapper = (props: Props) => {
  const [recordingRuleId, setRecordingRuleId] = useState("");
  const [isOpenEditRecordingRuleDialog, setIsOpenEditRecordingRuleDialog] =
    useState(false);
  const [isOpenAddRecordingRuleDialog, setIsOpenAddRecordingRuleDialog] =
    useState(false);

  const dispatch = useDispatch();
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.recordingRules);

  const { data, isLoading, isFetching } = useGetAllRecordingRulesQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["studentIds"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });
  const [deleteRecordingRule] = useDeleteRecordingRulesMutation();

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (recordingRuleId: string) => {
    return [
      {
        label: (
          <div className="flex items-center gap-2 text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setRecordingRuleId(recordingRuleId);
          setIsOpenEditRecordingRuleDialog(true);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.RECORDINGRULES,
      },
      {
        label: (
          <div className="flex items-center gap-2 font-semibold text-red-600">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure, you want to Delete this Recording Rule ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteRecordingRule(recordingRuleId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.RECORDINGRULES,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "batchName",
      headerName: "Batch Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
    },
    {
      field: "studentIds",
      headerName: "Students",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row) => {
        return <div>{row?.studentIds?.length}</div>;
      },
    },
    {
      field: "recordingDate",
      headerName: "Recording Date",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row) => {
        return (
          <div>
            {row?.recordingDate === "*" ? (
              <span className="text-xs font-medium">All Dates In Range</span>
            ) : (
              <span className="text-[14px]">
                {moment(row?.recordingDate).format("DD MMM yyyy")}{" "}
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Satus",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row) => {
        return (
          <div>
            {row?.startDate === "*" ? (
              <div className="px-4 py-1 text-xs text-white bg-green-500 rounded-xl">
                Always
              </div>
            ) : (
              <div className="px-4 py-1 text-xs text-white rounded-xl bg-primary-dark">
                Range
              </div>
            )}
          </div>
        );
      },
    },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row) => {
        return (
          <div>
            {row?.startDate
              ? row?.startDate === "*"
                ? "-"
                : formatedDateTimeIntoIst(row?.startDate, "DD MMM yyyy")
              : "-"}
          </div>
        );
      },
    },
    {
      field: "endDate",
      headerName: "End Date",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row) => {
        return (
          <div>
            {row?.endDate
              ? row?.endDate === "*"
                ? "-"
                : formatedDateTimeIntoIst(row?.endDate, "DD MMM yyyy")
              : "-"}
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Visible",
      flex: "flex-[1_1_0%]",
      extraClasses: "capitalize",
      renderCell: (row) => {
        return <div>{row?.status === "ALLOWED" ? "Allowed" : "Denied"}</div>;
      },
    },

    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
      renderCell: (row) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  return (
    <div>
      <RecordingRulesListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.RECORDINGRULES,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
        })}
        rows={items}
        onAddNew={() => setIsOpenAddRecordingRuleDialog(true)}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {isOpenAddRecordingRuleDialog ? (
        <AddRecordingRulesFormWrapper
          onClose={() => setIsOpenAddRecordingRuleDialog(false)}
        />
      ) : null}
      {isOpenEditRecordingRuleDialog ? (
        <EditRecordingRulesFormWrapper
          recordingRuleId={recordingRuleId}
          onClose={() => setIsOpenEditRecordingRuleDialog(false)}
        />
      ) : null}
    </div>
  );
};

export default RecordingRulesListingWrapper;
